
import React, { Component, createRef } from 'react';
import cx from 'classnames';
import _memoize from 'lodash.memoize';
import Tag from '../../Tag';
import {
  TAG_INPUT_CONTAINER,
  DISABLED_TAG_INPUT_CONTAINER,
  TAG_INPUT,
  TAG,
  TAG_INPUT_ERROR,
  TAG_INPUT_MAX,
  SELECTED_TAG_INPUT_CONTAINER } from
'../styles/tagInput';
import LabelWrapper from '../../LabelWrapper';
import ErrorIcon from '../../ErrorIcon';
import Text from '../../Text';

const TAG_JOIN = ',';
const BACKSPACE = 8;
const RETURN = 13;
const LF = 10;
const SELECT_ALL = 65;

























export default class TagInput extends Component {


  deleteTag = _memoize((tagName) => () => {
    const { tags } = this.state;

    const index = tags.findIndex(({ value }) => value === tagName);

    if (index <= -1) {return;}

    tags.splice(index, 1);

    this.setState({ tags });

    this.onChange(tags.map(({ value }) => value).join(TAG_JOIN));
  });

  static defaultProps = {
    defaultValue: ''
  };

  constructor(props) {
    super(props);

    const { defaultValue } = props;

    this.state = {
      valid: true,
      text: '',
      selectAll: false,
      tags: defaultValue ?
      defaultValue.split(TAG_JOIN).map((value) => ({ value, valid: true })) :
      []
    };

    this._tagInput = createRef();
  }

  componentDidMount = () => {
    this.validateDefaultValue();
  };

  validateDefaultValue = async () => {
    const { validate, tagValidation } = this.props;
    const { tags } = this.state;

    if (!tags.length || !validate || !tagValidation) {return;}

    const validatedTags = await Promise.all(tags.map(async ({ value }) => ({
      value,
      valid: await this.validateTag(value)
    })));

    this.setState({ tags: validatedTags }, this.handleValidate);
  };

  onChange = (tagString) => {
    const { onChange } = this.props;

    this.handleValidate();

    if (typeof onChange !== 'function') {return;}

    onChange(tagString);
  };

  addCurrentTag = (text, tags) => new Promise(async (resolve) => {
    const { maxTags } = this.props;

    const trimmedValue = text.trim();

    // No text to be turned into tag
    if (!text.trim()) {return;}

    // Remove tag if it already exists - case-insensitive
    const upperCaseTags = tags.map(({ value }) => value.toUpperCase());
    const index = upperCaseTags.indexOf(trimmedValue.toUpperCase());

    if (index === -1 && maxTags && tags.length >= maxTags) {
      return;
    }

    if (index > -1) {tags.splice(index, 1);}

    const newTagValid = await this.validateTag(trimmedValue);

    // Add new tag
    tags.push({ value: trimmedValue, valid: newTagValid });

    // reset textarea content
    this.setState({ text: '', tags }, resolve);

    if (!this._tagInput || !this._tagInput.current) {return;}

    this._tagInput.current.scrollTo({
      top: this._tagInput.current.scrollHeight,
      behavior: 'smooth'
    });
  });

  handleKeyDown = async (event) => {
    const { tags, text, selectAll } = this.state;
    const { keyCode, currentTarget } = event;
    const { selectionEnd } = currentTarget;

    if ((event.ctrlKey || event.metaKey) && keyCode === SELECT_ALL) {
      return void this.setState({ selectAll: true });
    }

    this.setState({ selectAll: false });

    // Not a special character
    if (keyCode !== RETURN && keyCode !== LF && keyCode !== BACKSPACE) {return;}

    // Backspacing with everything selected
    if (keyCode === BACKSPACE && selectAll) {
      return void this.setState({ tags: [], text }, () => {
        this.onChange('');
      });
    }

    // Backspacing within text
    if (keyCode === BACKSPACE && selectionEnd !== 0) {return;}

    // Backspacing over tag - delete tag
    if (keyCode === BACKSPACE && tags.length) {
      this.deleteTag(tags.pop().value)();
    }

    // Return - add tag and clear
    if (keyCode === RETURN || keyCode === LF) {
      await this.addCurrentTag(text, tags);
    }

    this.handleTagChange(tags);
  };

  handleInputChange = ({ currentTarget: { value } }) => {
    const text = value
    // These symbols are word boundaries
    .replace(/[,;:?! ]+/gi, ' ')
    // Accept only alphanumeric, at sign, dot, hyphen, and apostrophe
    .replace(/[^a-zA-Z0-9 @.'-_+]+/gi, '');

    this.setState({ text });
  };

  validateTag = async (value) => {
    const { tagValidation } = this.props;

    if (!tagValidation) {return true;}

    return tagValidation(value);
  };

  handleValidate = () => {
    const { validate } = this.props;
    const { tags } = this.state;

    const allTagsValid = tags.every(({ valid }) => valid);

    this.setState({ valid: allTagsValid, selectAll: false });

    if (!validate) {return;}

    validate(allTagsValid);
  };

  onBlur = async () => {
    const { maxTags } = this.props;
    const { text, tags: oldTags } = this.state;

    if (!text || text && maxTags === oldTags.length) {return void this.handleValidate();}

    await this.addCurrentTag(text, oldTags);

    this.handleValidate();

    const { tags } = this.state;

    this.handleTagChange(tags);
  };

  handleTagChange(tags) {
    const updatedTagsStr = tags.map(({ value }) => value).join(TAG_JOIN);

    this.onChange(updatedTagsStr);
  }

  render() {
    const { text, valid, tags, selectAll } = this.state;
    const {
      id,
      label,
      className,
      placeholder,
      disabled,
      errorMessage,
      labelProps,
      maxTags
    } = this.props;

    return (
      <LabelWrapper {...labelProps} id={id} label={label} className={className}>
        <div
          ref={this._tagInput}
          className={cx(
            'tag-input',
            TAG_INPUT_CONTAINER({ valid, disabled }),
            className,
            {
              'tag-input__disabled': disabled,
              'tag-input--selected': selectAll,
              [DISABLED_TAG_INPUT_CONTAINER]: disabled,
              [SELECTED_TAG_INPUT_CONTAINER]: selectAll
            }
          )}>

          {
          tags && tags.length ?
          tags.map(({ value, valid: tagValid }) =>
          <Tag
            key={value}
            tagName={value}
            onClick={this.deleteTag(value)}
            valid={tagValid}
            className={TAG}
            disabled={disabled} />

          ) :
          null}

          <input
            placeholder={placeholder}
            type="text"
            value={text}
            className={cx(TAG_INPUT)}
            onChange={this.handleInputChange}
            onKeyDown={this.handleKeyDown}
            onBlur={this.onBlur}
            disabled={disabled} />

          {
          maxTags && valid && !disabled ?

          <Text noMargin className={cx(TAG_INPUT_MAX, 'tag-input__max')}>
                  {`${tags.length}/${maxTags}`}
                </Text> :

          null}

          {
          !valid ?

          <ErrorIcon
            id="tag-input-error-icon"
            className={cx(TAG_INPUT_ERROR, 'tag-input__error')}
            errorMessage={errorMessage}
            place="left" /> :


          null}

        </div>
      </LabelWrapper>);

  }
}